import { FC, useCallback, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useForm } from 'react-hook-form';

import OnGoLayout from '../OnGoLayout';
import OnGoInitializeFormContent from './OnGoInitializeFormContent';
import { ONGO_INIT_FORM_ID } from './onGoInitialize.constants';
import type { OnGoInitializeFormValues, OnGoInitializeProps } from './onGoInitialize.types';
import { prepareDefaultScope } from './utils';
import useSubjectStore from 'storages/subject';
import { useMutation, useQueryClient } from 'react-query';
import exerciseQueuesApi, { exerciseQueuesApiKeys } from 'api/rest/exerciseQueues/exerciseQueues';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import { resolveQueueErrorMessage } from './resolveQueueErrorMessage';
import FormErrorBanner from './FormErrorBanner';

export type { OnGoInitializeFormValues } from './onGoInitialize.types';

const OnGoInitialize: FC<OnGoInitializeProps> = ({ sectionsData }) => {
  const [submitBlockedUntilValid, setSubmitBlockedUntilValid] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    formState: { isValid },
  } = useForm<OnGoInitializeFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      scope: prepareDefaultScope(sectionsData),
      sort: 'section_index',
      unansweredOnly: true,
      includeCorrectlyAnswered: true,
      includeIncorrectlyAnswered: true,
      queueSize: 20,
      useAllExercises: false,
    },
  });

  const { subject } = useSubjectStore();
  const queryClient = useQueryClient();
  const { logEvent } = useUserLogContext();

  const createQueueMutation = useMutation(exerciseQueuesApi.create(), {
    onSuccess: queue => {
      if (subject) {
        queryClient.invalidateQueries(exerciseQueuesApiKeys.active(subject.pk));
        logEvent('on-go-queue-create', {
          subjectPk: subject.pk,
          subjectCode: subject.subjectCode,
          queueUuid: queue.uuid,
          sort: getValues('sort'),
          size: getValues('useAllExercises') ? null : getValues('queueSize'),
        });
      }
    },
  });

  const onSubmit = async (values: OnGoInitializeFormValues) => {
    if (!subject) {
      return;
    }
    try {
      await createQueueMutation.mutateAsync({ subject, ...values });
    } catch {
      // Error is already exposed via createQueueMutation.error; swallow to avoid unhandled rejection.
    }
  };

  const onInvalidSubmit = useCallback(() => {
    setSubmitBlockedUntilValid(true);
  }, []);

  const isSubmitting = createQueueMutation.isLoading;
  const startDisabled = (submitBlockedUntilValid && !isValid) || isSubmitting;
  const errorMessage = createQueueMutation.isError ? resolveQueueErrorMessage(createQueueMutation.error) : null;

  return (
    <OnGoLayout
      footer={
        <Button
          color='primary'
          disabled={startDisabled}
          endIcon={isSubmitting ? <CircularProgress color='inherit' size={18} /> : undefined}
          form={ONGO_INIT_FORM_ID}
          fullWidth
          size='large'
          type='submit'
          variant='contained'
        >
          {isSubmitting ? 'Tworzymy kolejkę…' : 'Zaczynamy'}
        </Button>
      }
    >
      <form id={ONGO_INIT_FORM_ID} noValidate onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
        {errorMessage ? (
          <Box mb={2}>
            <FormErrorBanner message={errorMessage} />
          </Box>
        ) : null}
        <OnGoInitializeFormContent
          control={control}
          getValues={getValues}
          sectionsData={sectionsData}
          setValue={setValue}
          trigger={trigger}
        />
      </form>
    </OnGoLayout>
  );
};

export default OnGoInitialize;
